import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { FundPerformance } from '../../../../../dto/FundPerformance';
import { ModalButtons } from '../../../../../../shared/component/modal-container/modal-container.component';
import { FundPerformanceBackendService } from '../../../../../service/fund-performance-backend.service';
import { FpCommitmentAllocationDTO } from './dto/FpCommitmentAllocationDTO';


@Component({
  selector: 'app-fp-commitment-allocation',
  templateUrl: './fp-commitment-allocation.component.html',
  styleUrls: ['./fp-commitment-allocation.component.scss'],
})
export class FpCommitmentAllocationComponent implements OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  @Input() fundPerformance: FundPerformance;
  @Output() fundPerfSetter = new EventEmitter<FundPerformance>();
  @Output() isConfirmedUpdate = new EventEmitter<void>();

  fpGroup: FormGroup;
  opExClass = {
    type: '',
    bottomText: '',
  };

  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  expectedOpEx: number;
  allocationCommitmentTitle = 'Unallocated commitment';
  expectedFutureInvPrefix = '-';

  constructor(private fb: FormBuilder, private fpService: FundPerformanceBackendService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fundPerformance && this.fundPerformance) {
      this.createForm(this.fundPerformance);
      if (this.fundPerformance.expectedOpEx) {
        this.fpGroup.patchValue({
          expectedOpEx: (this.fundPerformance.expectedOpEx ? Math.abs(this.fundPerformance.expectedOpEx * 100) : null),
        });
      }
      if (this.fundPerformance.expectedFutureInv) {
        this.fpGroup.patchValue({
          expectedFutureInv: (this.fundPerformance.expectedFutureInv ? Math.abs(this.fundPerformance.expectedFutureInv) : null),
        });
      }
    }
    this.checkOpExValue(this.fundPerformance.expectedOpEx);
    this.count();
  }


  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  commitmentCount(value: string): void {
    if (value && value !== '-') {
      const tempValue = Math.abs(parseInt(value.replace(/,/g, ''), 10));
      this.fpGroup.patchValue({ totalCommitment: tempValue });
    }
    this.checkOpExValue(this.expectedOpEx ?? this.fundPerformance.expectedOpEx);
    this.count();
  }

  expectedOpExCount(value: string): void {
    this.expectedOpEx = Number(value.slice(0, -1)) / 100;
    this.checkOpExValue(this.expectedOpEx);
    this.count();
  }

  checkOpExValue(opExValue: number): void {
    if (opExValue * this.fpGroup.value.totalCommitment < Math.abs(this.fundPerformance.totalExpenses)) {
      this.opExClass = {
        type: 'error',
        bottomText: 'OpEx should be greater than ' +
           (Math.abs(this.fundPerformance.totalExpenses) / this.fpGroup.value.totalCommitment * 100).toFixed(2) + '%',
      };
    } else {
      this.opExClass = {
        type: '',
        bottomText: '',
      };
    }
  }

  expectedFutureInvCount(value: string): void {
    if (value && value !== '-') {
      const tempValue = Math.abs(parseInt(value.replace(/,/g, ''), 10));
      this.fpGroup.patchValue({ expectedFutureInv: tempValue });
    }
    this.count();
  }

  count(): void {
    if (this.fpGroup.value.totalCommitment) {
      this.fpGroup.patchValue({
        allocationCommitment:
          (this.fpGroup.value.totalCommitment ? this.fpGroup.value.totalCommitment : 0) *
          (1 - Math.abs(this.fpGroup.value.expectedOpEx ? this.fpGroup.value.expectedOpEx / 100 : 0)) -
          Math.abs(this.fpGroup.getRawValue().totalCost ?? 0) -
          Math.abs(this.fpGroup.value.expectedFutureInv ?? 0),
      });
    }
    this.checkTitleAndPrefix();
  }

  checkTitleAndPrefix(): void {
    this.expectedFutureInvPrefix = this.fpGroup.get('expectedFutureInv').getRawValue() ? '-' : '';
    this.allocationCommitmentTitle = this.fpGroup.get('allocationCommitment').getRawValue() > 0 ? 'Unallocated commitment' :
      'Unallocated commitment / (Above commitment)';
  }

  checkApplyActivated(): boolean {
    const { totalCommitment, expectedOpEx } = this.fpGroup.getRawValue();
    return (totalCommitment >= this.fundPerformance.contribution) &&
           ((expectedOpEx / 100 * totalCommitment) > Math.abs(this.fundPerformance.totalExpenses));
  }

  async confirmUpdateFundPerformance(): Promise<void> {
    this.fpGroup.patchValue({
      expectedOpEx: this.expectedOpEx ?? this.fundPerformance.expectedOpEx,
    });
    this.fundPerformance = await firstValueFrom(this.fpService.updateCommitmentAllocation(this.fpGroup.value as FpCommitmentAllocationDTO));
    this.fundPerfSetter.emit(this.fundPerformance);
    this.close();
  }

  close(): void {
    this.isConfirmedUpdate.emit();
  }

  private createForm(fp: FundPerformance): void {
    this.fpGroup = this.fb.group({
      id: new FormControl(fp.id),
      totalCommitment: new FormControl(fp.totalCommitment),
      totalCost: new FormControl({ value: -fp.totalCost, disabled: true }),
      expectedFutureInv: new FormControl(fp.expectedFutureInv),
      expectedOpEx: new FormControl(Math.abs(fp?.expectedOpEx * 100)),
      allocationCommitment: new FormControl({ value: undefined, disabled: true }),
    });
  }
}

